import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="pt-24 pb-10 text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-secondary-100 rounded-full">
            <i className="ri-emotion-sad-line text-3xl text-secondary-500"></i>
          </div>
          <h1 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该文章不存在或已被删除</p>
          <Link
            to="/"
            className="inline-flex px-5 py-2.5 bg-primary-500 text-white font-medium rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: '黑料导航',
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料导航',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://heiliaodaohang.com/article/${article.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 黑料导航`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},黑料导航,娱乐黑料,明星黑料`}
        canonicalUrl={`https://heiliaodaohang.com/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        schema={articleSchema}
      />

      <Navbar />

      <main className="pt-20 md:pt-24 pb-10">
        <article className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-200 transition-colors"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  {article.category}
                </Link>
                {article.hot && (
                  <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                    <i className="ri-fire-line mr-1"></i>热榜
                  </span>
                )}
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line"></i>
                  </div>
                  {article.publishDate}
                </span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万次阅读` : `${article.views}次阅读`}
                </span>
              </div>

              <h1 className="text-xl md:text-3xl font-heading text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <p className="text-base text-foreground-600 leading-relaxed mb-6 p-4 bg-background-100 rounded-xl border-l-4 border-primary-500">
                {article.excerpt}
              </p>
            </div>

            <div className="w-full aspect-[16/9] rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 黑料导航`}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none mb-8 text-foreground-800 leading-loose"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mb-10 pt-6 border-t border-background-200">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between py-6 border-t border-background-200 mb-10">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-2 px-4 py-2 bg-background-100 rounded-full text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-thumb-up-line"></i>
                  </div>
                  点赞
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-background-100 rounded-full text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-share-forward-line"></i>
                  </div>
                  分享
                </button>
              </div>
              <button className="flex items-center gap-2 px-4 py-2 bg-background-100 rounded-full text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-bookmark-line"></i>
                </div>
                收藏
              </button>
            </div>

            {relatedArticles.length > 0 && (
              <section className="mb-10">
                <h3 className="text-lg font-heading text-foreground-900 mb-5">相关推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden group hover:border-primary-200 transition-colors cursor-pointer"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      <div className="w-full aspect-[16/9] overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-400 mt-1">{ra.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}